<template>
  <v-container style="max-width: 650px;">
    <PageHeader />

    <v-card
      class="mx-auto"
      max-width="1000"
    >
      <v-card-title
        class="d-flex justify-space-between align-center"
      >
        <div class="d-flex align-center">
          Personal Info
          <v-img
            class="mx-5"
            contain
            src="@/assets/settings/personal_info.png"
            max-width="45"
            max-height="45"
          />
        </div>

        <div>
          <div style="text-align: right;">
            <v-btn
              v-if="!editFlag"
              color="primary"
              :loading="isSaving"
              @click="editFlag = true"
            >
              Edit
              <v-icon
                small
              >
                create
              </v-icon>
            </v-btn>
            <v-btn
              v-else
              :loading="isSaving"
              @click="save"
            >
              Save
              <v-icon
                small
              >
                create
              </v-icon>
            </v-btn>
          </div>
        </div>
      </v-card-title>

      <v-card-text v-if="profile">
        <v-form ref="settingForm">
          <v-row class="mx-0">
            <v-col
              cols="12"
              sm="8"
              order="2"
              order-sm="1"
            >
              <v-text-field
                v-model="profile.first_name"
                :rules="apiRules.first_name || rules.name"
                :disabled="!editFlag"
                label="First Name"
                required
                @keyup="apiRuleClear('first_name')"
              />

              <v-text-field
                v-model="profile.last_name"
                :rules="apiRules.last_name || rules.name"
                :disabled="!editFlag"
                label="Last Name"
                required
                @keyup="apiRuleClear('last_name')"
              />

              <v-text-field
                v-model="profile.email"
                :rules="apiRules.email || rules.email"
                label="E-mail"
                :disabled="true"
                @keyup="apiRuleClear('email')"
              />

              <v-autocomplete
                v-model="profile.time_zone"
                label="Time zone"
                :rules="apiRules.time_zone || rules.timezone"
                required
                :disabled="!editFlag"
                :items="timezoneList"
                item-text="display"
                item-value="value"
                @keyup="apiRuleClear('time_zone')"
              />
            </v-col>

            <v-col
              cols="12"
              sm="4"
              order="1"
              order-sm="2"
              align-self="center"
              class="d-flex justify-center"
            >
              <v-avatar
                size="130"
                :color="!profile.profile_image ? 'primary lighten-1' : ''"
              >
                <v-img
                  v-if="profile.profile_image"
                  :src="profile.profile_image"
                  :lazy-src="profile.profile_image"
                  alt="Profile"
                >
                  <template #placeholder>
                    <v-row
                      class="fill-height ma-0"
                      align="center"
                      justify="center"
                    >
                      <v-progress-circular
                        indeterminate
                        color="grey lighten-5"
                      />
                    </v-row>
                  </template>
                </v-img>

                <span
                  v-else
                  class="white--text display-1"
                >
                  {{ initials }}
                </span>
              </v-avatar>

              <v-btn
                v-if="editFlag"
                color="primary"
                fab
                x-small
                dark
                class="align-self-end ml-n5"
                @click="imageDialog = true"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <ImageProfileUpload
      v-if="profile"
      :dialog.sync="imageDialog"
      :uuid="profile.uuid"
      :image.sync="profile.profile_image"
    />
  </v-container>
</template>

<script>
import accountApi from '@/api/account/account';
import apiRules from '@/mixins/apiRules';
import { mapState, mapMutations } from 'vuex';
import ImageProfileUpload from '../widget/ImageProfileUpload.vue';
import PageHeader from '../widget/PageHeader.vue';

export default {
  name: 'ProfileInfo',

  components: {
    ImageProfileUpload,
    PageHeader,
  },

  mixins: [
    apiRules,
  ],

  data: () => ({
    profile: null,
    editFlag: false,
    isSaving: false,
    imageDialog: false,
    passwordDialog: false,
    timezoneList: [],
    rules: {
      name: [
        (v) => !!v || 'Name is required.',
      ],
      email: [
        (v) => !!v || 'E-mail is required.',
        (v) => /.+@.+\..+/.test(v) || 'E-mail must be valid',
      ],
      timezone: [
        (v) => !!v || 'Time zone is required.',
      ],
    },
  }),

  computed: {
    ...mapState({
      user: (state) => state.account.user,
    }),

    initials() {
      if (!this.user.first_name && !this.user.last_name) return 'X_X';
      let init1 = '';
      let init2 = '';
      if (this.user.first_name) {
        init1 = this.user.first_name[0].substring(0, 1).toUpperCase();
      }
      if (this.user.last_name) {
        init2 = this.user.last_name[0].substring(0, 1).toUpperCase();
      }
      return `${init1} ${init2}`;
    },
  },

  created() {
    this.initProfile();
    this.getTimezone();
  },

  methods: {
    ...mapMutations('account', [
      'userUpdated',
    ]),

    initProfile() {
      this.profile = { ...this.user };
    },

    async getTimezone() {
      try {
        this.timezoneList = await accountApi.getTimezone();
      } catch (err) {
        console.log(err);
        this.$toast.error(err.msg || err.message);
      }
    },

    async save() {
      if (this.$refs.settingForm.validate()) {
        try {
          this.isSaving = true;
          const sendData = JSON.parse(JSON.stringify(this.profile));
          delete sendData.profile_image;
          const response = await accountApi.updateUserProfile(sendData);
          this.profile = response;
          this.userUpdated(response);
          this.editFlag = false;
          this.$toast.success('Changes successfully saved.');
        } catch (err) {
          console.log(err);
          if (err.field) {
            this.apiRules = err.field;
            if (!this.isErrorMatchingField(this.profile)) {
              this.$toast.error(this.apiErrorMsg);
            }
            this.$vuetify.goTo(0, {
              duration: 700,
              offset: 0,
              easing: 'easeInOutCubic',
            });
          } else {
            this.$toast.error(err.msg);
          }
        } finally {
          this.isSaving = false;
        }
      }
    },
  },
};
</script>
